/**
 * Correspondence Query Keys
 *
 * Centralized query key factory for all correspondence-related queries.
 */

import type { CorrespondenceFilters } from './types';
import type { WorkflowType } from '@/lib/services/correspondences/workflows/workflow-config';

/**
 * Correspondence query key factory
 *
 * @example
 * ```typescript
 * // Get matter correspondence
 * useQuery({ queryKey: correspondenceKeys.forMatter(matterId), queryFn: ... })
 *
 * // Get institution correspondence
 * useQuery({
 *   queryKey: correspondenceKeys.forInstitution(matterId, institutionId),
 *   queryFn: ...
 * })
 * ```
 */
export const correspondenceKeys = {
  /**
   * Root key for all correspondence queries
   */
  all: ['correspondence'] as const,

  /**
   * Base key for correspondence lists
   */
  lists: () => [...correspondenceKeys.all, 'list'] as const,

  /**
   * Correspondence for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) =>
    [...correspondenceKeys.lists(), matterId] as const,

  /**
   * Filtered correspondence list
   * @param matterId - The matter ID
   * @param filters - Optional filters
   */
  list: (matterId: number, filters?: CorrespondenceFilters) =>
    filters
      ? ([...correspondenceKeys.forMatter(matterId), filters] as const)
      : correspondenceKeys.forMatter(matterId),

  /**
   * Correspondence for an institution
   * @param matterId - The matter ID
   * @param institutionId - The institution ID
   */
  forInstitution: (matterId: number, institutionId: number) =>
    [...correspondenceKeys.all, matterId, institutionId] as const,

  /**
   * Base key for correspondence details
   */
  details: () => [...correspondenceKeys.all, 'detail'] as const,

  /**
   * Specific correspondence detail
   * @param correspondenceId - The correspondence ID
   */
  detail: (correspondenceId: number) =>
    [...correspondenceKeys.details(), correspondenceId] as const,

  /**
   * Correspondence with matter context
   * @param matterId - The matter ID
   * @param correspondenceId - The correspondence ID
   */
  item: (matterId: number, correspondenceId: number) =>
    [
      ...correspondenceKeys.forMatter(matterId),
      'item',
      correspondenceId
    ] as const,

  /**
   * Correspondence thread
   * @param correspondenceId - The correspondence ID
   */
  thread: (correspondenceId: number) =>
    [...correspondenceKeys.detail(correspondenceId), 'thread'] as const,

  /**
   * Correspondence threads for a matter
   * @param matterId - The matter ID
   */
  threads: (matterId: number) =>
    [...correspondenceKeys.forMatter(matterId), 'threads'] as const,

  /**
   * Draft correspondences
   * @param matterId - The matter ID
   */
  drafts: (matterId: number) =>
    [...correspondenceKeys.forMatter(matterId), 'drafts'] as const,

  /**
   * Workflow correspondences
   * @param matterId - The matter ID
   */
  workflow: (matterId: number) =>
    ['workflow', 'correspondences', matterId] as const,

  /**
   * Workflow data for correspondence
   * @param matterId - The matter ID
   * @param type - Workflow type (notification, collection-request, follow-up)
   */
  workflowData: (matterId: number, type: WorkflowType) =>
    ['workflow', 'data', matterId, type] as const,

  /**
   * Letter preview PDF for a correspondence
   * @param matterId - The matter ID
   * @param correspondenceId - The correspondence ID
   */
  letterPreview: (matterId: number, correspondenceId: number) =>
    [
      ...correspondenceKeys.all,
      'letter-preview',
      matterId,
      correspondenceId
    ] as const,

  /**
   * The resolved letter sign-off (signature + name/title-or-footer) for a
   * correspondence — the read-only preview shown below the editable letter
   * body (VALE-687). Distinct from `letterPreview`: this is a small resolved
   * JSON payload, not a rendered PDF, so it is worth its own key rather than
   * riding the PDF's cache lifecycle.
   * @param matterId - The matter ID
   * @param correspondenceId - The correspondence ID
   */
  letterSignOffPreview: (matterId: number, correspondenceId: number) =>
    [
      ...correspondenceKeys.all,
      'letter-sign-off-preview',
      matterId,
      correspondenceId
    ] as const,

  /**
   * The resolved email signature for a correspondence — the read-only
   * preview shown below the editable email body (VALE-1070, the email-tab
   * counterpart of `letterSignOffPreview`).
   * @param matterId - The matter ID
   * @param correspondenceId - The correspondence ID
   */
  emailSignOffPreview: (matterId: number, correspondenceId: number) =>
    [
      ...correspondenceKeys.all,
      'email-sign-off-preview',
      matterId,
      correspondenceId
    ] as const,

  /**
   * Everything that renders WHO SIGNS this correspondence, in one place.
   *
   * Changing the signatory moves three surfaces, and only one of them announces
   * itself: the PDF preview has a nonce the drawer bumps, while the letter and
   * email sign-off blocks are ordinary queries keyed on (matter,
   * correspondence) with no signatory in the key. Nothing about a local
   * selection makes those refetch, so before VALE-1163 picking a different
   * signatory left their signature image and firm footer showing the previous
   * person until the drawer was closed and reopened.
   *
   * Enumerated here rather than at the call site so the set has one definition:
   * a fourth signatory-dependent query is added here and every invalidator gets
   * it. `__tests__/signatory-change-invalidation.test.ts` reconciles it against
   * the queries the drawer actually reads.
   */
  signatoryDependent: (matterId: number, correspondenceId: number) =>
    [
      correspondenceKeys.item(matterId, correspondenceId),
      correspondenceKeys.letterSignOffPreview(matterId, correspondenceId),
      correspondenceKeys.emailSignOffPreview(matterId, correspondenceId)
    ] as const,

  /**
   * Who will sign letters on a matter, under the firm's configured rule — the
   * matter-level counterpart of `letterSignOffPreview`, for the bulk send
   * dialogs that choose a signatory before any draft exists (VALE-1012).
   * @param matterId - The matter ID
   */
  matterDefaultSignatory: (matterId: number) =>
    [...correspondenceKeys.all, 'matter-default-signatory', matterId] as const,

  /**
   * AI analysis preview for a reply correspondence (LF-3317). Driven by
   * `useAiAnalysisPreview` and polled while `analysisStatus === 'pending'`.
   * @param correspondenceId - The correspondence ID
   */
  aiAnalysisPreview: (correspondenceId: number) =>
    [
      ...correspondenceKeys.detail(correspondenceId),
      'ai-analysis-preview'
    ] as const,

  /**
   * Follow-up workflow data
   * @param matterId - The matter ID
   * @param institutionId - The institution ID
   */
  followUpWorkflow: (matterId: number, institutionId: number) =>
    ['follow-up-workflow', matterId, institutionId] as const,

  /**
   * Correspondence exports
   * @param matterId - The matter ID
   * @param correspondenceId - The correspondence ID
   */
  exports: (matterId: number, correspondenceId: number) =>
    ['correspondence-exports', 'list', matterId, correspondenceId] as const,

  /**
   * Export status
   * @param matterId - The matter ID
   * @param correspondenceId - The correspondence ID
   * @param exportId - The export ID
   */
  exportStatus: (
    matterId: number,
    correspondenceId: number,
    exportId: number
  ) =>
    [
      'correspondence-exports',
      'status',
      matterId,
      correspondenceId,
      exportId
    ] as const
} as const;
